import React, { useState } from 'react'
import QRCode from 'qrcode.react'
import Tooltip from 'components/Tooltip'
import useHandleCopyValue from 'utils/hooks/useHandleCopyValue'

// Icon
import Logo from 'icons/logo-qr-code.svg'

// Components
import Link from 'components/Link'

// Styles
import {
  Title,
  Description,
  Code,
  Columns,
  Column,
  List,
  Item,
} from '../Setup2FAModal.styles'

// Types
interface QRCodeStepProps {
  otpAuthUri: string
}

// Helpers
const QR_CODE_OPTIONS = {
  src: Logo,
  width: 52,
  height: 52,
  excavate: true,
}

/**
 * QRCodeStep
 *
 * Renders the second step for the 2FA Setup Modal
 */
export const QRCodeStep: React.FC<QRCodeStepProps> = ({ otpAuthUri }) => {
  const [showQRCode, setShowQRCode] = useState(true)

  // Get the secret from the full URI
  const secret = otpAuthUri?.split('=')[2]

  const {
    handleCopyValue,
    copyTooltipText,
    handleEnterCopiableArea,
    handleLeaveCopiableArea,
    copyTooltipVisible,
  } = useHandleCopyValue(secret.replace(/\s/g, ''))

  const handleMouseEnterLabel = () =>
    !secret ? null : handleEnterCopiableArea()
  const handleMouseLeaveLabel = () =>
    !secret ? null : handleLeaveCopiableArea()

  return (
    <>
      <Title>Scan the QR Code</Title>
      <Description>
        Next, we’ll link your Sketch account to the authenticator app you just
        installed.
      </Description>
      {showQRCode ? (
        <>
          <Columns>
            <Column>
              <QRCode
                data-testid="qr-code-image"
                value={otpAuthUri}
                imageSettings={QR_CODE_OPTIONS}
                renderAs="svg"
                level="H"
                size={144}
              />
            </Column>
            <List>
              <Item>Open the authenticator app </Item>
              <Item>Tap Scan a QR code </Item>
              <Item>Scan this code with your phone camera </Item>
            </List>
          </Columns>
          <Link onClick={() => setShowQRCode(false)} isUnderlined external>
            Enter the code manually instead
          </Link>
        </>
      ) : (
        <>
          <Code
            onClick={handleCopyValue}
            onMouseEnter={handleMouseEnterLabel}
            onMouseLeave={handleMouseLeaveLabel}>
            <Tooltip
              placement="bottom"
              visible={copyTooltipVisible}
              content={copyTooltipText}>
              {secret}
            </Tooltip>
          </Code>
          <Link onClick={() => setShowQRCode(true)} isUnderlined external>
            Scan the code instead
          </Link>
        </>
      )}
    </>
  )
}
